import { Component, OnInit, Input, OnDestroy } from '@angular/core';
import { BehaviorSubject, Subscription, Observable } from "rxjs";
import { tap, switchMap } from "rxjs/operators";
import { Well, WellLogSearchSpec, WellLog, Form, ComplianceSearchSpec, Compliance, WellStimulation, WellStimulationSearchSpec, InspectionSearchSpec, Inspection } from "../../../common/models/well.model";
import { WellService } from "../../../common/services/well.service";
import { GridFormatterService } from "../../../common/services/grid-formatter.service";
import { PermitService } from "../../../common/services/permit.service";
import { Permit, PermitSearchSpec } from "../../../common/models/permit.model";
import { AbstractControl, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Dialog, DialogButton, DialogControl, DialogControlType } from '../../../core/models/dialog.model';
import { DynamicDialogService } from '../../../core/services/dynamic-dialog.service';


@Component({
    selector: 'compliance-dates',
    templateUrl: './compliance-dates.component.html',
    styleUrls: ['./compliance-dates.component.scss'],
    standalone: false
})
export class ComplianceDatesComponent implements OnInit, OnDestroy {

    @Input() well: Well;
    @Input() canEdit: boolean = false;

    private inactiveStatuses: string[] = ['I1', 'I2', 'I3', 'I4'];
    private loading$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);
    private revisedPermitTypes: string[] = ['RU', 'RIPL', 'Permit to Revise Location'];
    private _compliance: Compliance[] = [];
    private _inspections: Inspection[] = [];
    private _logs: WellLog[] = [];
    private _permits: Permit[];
    private _stimulations: WellStimulation[] = [];


    subscription: Subscription;

    get asDrilledPlatFinal(): string {
        if (this.well == null || this.well.location == null) return "No";
        return (this.well.location.sourceOfLocation === 'S') ? "Yes" : "No";
    }

    get asDrilledPlatPreliminary(): string {
        if (this.well == null || this.well.location == null) return "No";
        return (this.well.location.sourceOfLocation === 'A') ? "Yes" : "No";
    }


    get isLoading(): boolean {
        return this.loading$.value;
    }

    get cementLogs(): WellLog {
        return this.findLogByType('CBL');
    }

    get chiefDesigneeDate(): Date {
        if (this._compliance == null || this._compliance.length === 0) return null;
        let matching: Compliance[] = this._compliance.filter(c => c.chiefOrder != null && c.dateEnforce != null);
        if (matching.length > 0) {
            matching = matching.sort((a, b) => {
                if (a.dateEnforce > b.dateEnforce) return -1;
                if (a.dateEnforce < b.dateEnforce) return 1;
                return 0;
            });
            return matching[0].dateEnforce;
        } else {
            return null;
        }
    }

    get deviationSurvey(): WellLog {
        return this.findLogByType('DIR');
    }

    get electricLogs(): WellLog {
        return this.findLogByType('EL');
    }

    get finalRestorationDate(): Date {
        return this.findStatusDate(["FR"]);
    }

    get isInFinalRestoration(): boolean {
        if (this.well.wellStatus.disposition == 'FR') return true;
        return false;
    }

    get form8A(): string {
        let value: string = "Unknown";
        if (this.well != null && this.well.forms != null) {
            const form8A = this.well.forms.find(f => f.name === 'Form8A');
            if (form8A != null) value = this.formatForm8AB(form8A);
        }
        return value;
    }

    get form8B(): string {
        let value: string = "Unknown";
        if (this.well != null && this.well.forms != null) {
            const form8B = this.well.forms.find(f => f.name === 'Form8B');
            if (form8B != null) value = this.formatForm8AB(form8B);
        }
        return value;
    }

    get notFoundDate(): Date {
        return this.findStatusDate(["NF"]);
    }

    get orphanInspectionDate(): Date {
        if (this._inspections == null || this._inspections.length === 0) return null;
        let matching: Inspection[] = this._inspections.filter(i => i.dateInspected != null);
        if (matching == null || matching.length === 0) return null;
        matching = matching.sort((a, b) => {
            if (a.dateInspected > b.dateInspected) return -1;
            if (a.dateInspected < b.dateInspected) return 1;
            return 0;
        });
        return matching[0].dateInspected;
    }

    get pressRateGraph(): string {
        if (this._stimulations == null || this._stimulations.length === 0) return "No";
        let invoices = this._stimulations.filter(s => s.pressRateGraph != null && s.pressRateGraph === true);
        return invoices.length > 0 ? "Yes" : "No";
    }

    get revisedUnitDate(): Date {
        if (this._permits == null || this._permits.length === 0) return null;
        let permit = this._permits[0];
        return (this._permits[0].dateIssued != null) ? this._permits[0].dateIssued : this._permits[0].applicationReceivedDate;
    }

    get revocationOfAuthorizationToInjectDate(): Date {
        if (this._compliance == null || this._compliance.length == 0) return null;
        let revocation: Compliance = this._compliance.find(c => c.dateRevocation != null && c.subjectLine != null && c.subjectLine.toLocaleLowerCase().includes("permit to inject brine"));
        if (revocation == null) return null;
        return revocation.dateRevocation;
    }

    get stimulationInvoice(): string {
        if (this._stimulations == null || this._stimulations.length === 0) return "No";
        let invoices = this._stimulations.filter(s => s.invoice != null && s.invoice === true);
        return invoices.length > 0 ? "Yes" : "No";
    }

    get temporaryInactiveDate(): Date {
        return this.findStatusDate(this.inactiveStatuses);
    }


    editorDialog: Dialog = new Dialog("<div class=\"bold\">Update Compliance Dates</div><div class=\"text-align-left\">Comments will be temporarily stored with the change until the change is saved using the \"Save\" button.  When the change is saved, comments will appear under the \"Well Notes\" tab.</div>", [new DialogButton("Save", true, (args) => this.saveUpdate(args)), new DialogButton("Cancel", false, () => { return; })], true, []);
    editorForm: FormGroup = new FormGroup({});
    constructor(private svc: WellService, private formatter: GridFormatterService, private permitService: PermitService, private builder: FormBuilder, private dialog: DynamicDialogService) { }

    ngOnInit() {

        const spec: any = {
            apiNumbers: [this.well.id]
        }

        const logObs$: Observable<WellLog[]> = this.svc.getWellLogs(<WellLogSearchSpec>spec)
            .pipe(
                tap((data: WellLog[]) => {
                    this._logs = data;
                })
            );
        const complianceObs$: Observable<Compliance[]> = this.svc.getCompliance(<ComplianceSearchSpec>spec)
            .pipe(
                tap((data: Compliance[]) => {
                    this._compliance = data;
                })
            );


        const stimulationObs$: Observable<WellStimulation[]> = this.svc.getStimulationRecords(<WellStimulationSearchSpec>spec).pipe(
            tap((data: WellStimulation[]) => {

                this._stimulations = data;
            })
        );

        const inspectionSpec: InspectionSearchSpec = {
            apiNumbers: [this.well.id],
            inspectionTypes: ['OR'],
            includeAddresses: false,
            includeLetters: false
        }

        const inspection$: Observable<Inspection[]> = this.svc.getInspections(inspectionSpec).pipe(
            tap((data: Inspection[]) => {

                this._inspections = data;
            })
        );

        const permitSpc: PermitSearchSpec = {
            hasAPINumber: true,
            ids: [],
            organizationId: null,
            apiNumber: this.well.id,
            cancellationStatus: false,
            includeProposedWell: false,
            includeRestorationPlan: false,
            includeCasingPlan: false,
            includeAccessRoutes: false,
            includeContacts: false,
            includeOtherWells: false,
            includeRUMAs: false,
            includeRoyaltyInterests: false,
            includeSectionApprovals: false,
            includeWaterSources: false,
            includeLocationGISPoints: false
        } as PermitSearchSpec;

        const permit$: Observable<Permit[]> = this.permitService.findPermits(permitSpc).pipe(
            tap((data: Permit[]) => {
                this._permits = data.filter(d => this.revisedPermitTypes.includes(d.type))
                    .sort((a, b) => {
                        if (a.dateIssued != null && b.dateIssued != null) {
                            if (a.dateIssued > b.dateIssued) return -1;
                            if (a.dateIssued < b.dateIssued) return 1;
                            return 0;
                        } else {
                            if (a.applicationReceivedDate > b.applicationReceivedDate) return -1;
                            if (a.applicationReceivedDate < b.applicationReceivedDate) return 1;
                            return 0;
                        }
                    });
            })
        )

        this.subscription = logObs$.pipe(
            switchMap(() => {
                return complianceObs$;
            }),
            switchMap(() => {
                return stimulationObs$;
            }),
            switchMap(() => {
                return inspection$;
            }),
            switchMap(() => {
                return permit$;
            })
        ).subscribe(
            (d) => { this.loading$.next(false); },
            (error) => {
                this.loading$.next(false);
            }
        );

        this.buildEditorForm();
    }

    ngOnDestroy() {
        if (this.subscription != null) this.subscription.unsubscribe();
    }

    private findLogByType(type: string): WellLog {
        let matches = this._logs.filter(l => l.logType === type && l.dateRun != null);
        if (matches.length > 0) {
            matches = matches.sort((a, b) => {
                if (a.dateRun > b.dateRun) return -1;
                if (a.dateRun < b.dateRun) return 1;
                return 0;
            });
            return matches[0];
        }
        return null;
    }

    buildEditorForm(): void {
        this.editorForm = this.builder.group({
            //Dates
            permitIssued: this.builder.group({ permitIssued: [this.well.constructionPermit.dateApproved, []], comments: [null, [Validators.required]] }),
            permitExpired: this.builder.group({ permitExpired: [this.well.constructionPermit.dateExpired, []], comments: [null, [Validators.required]] }),
            permitRevoked: this.builder.group({ permitRevoked: [this.well.dateOfRevocation, []], comments: [null, [Validators.required]] }),
            spudDate: this.builder.group({ spudDate: [this.well.dateWellDrillingStarted, []], comments: [null, [Validators.required]] }),
            dateTotalDepthReached: this.builder.group({ dateTotalDepthReached: [this.well.dateTotalDepthReached, []], comments: [null, [Validators.required]] }),
            dateOfFirstProduction: this.builder.group({ dateOfFirstProduction: [this.well.production.dateOfFirstProduction, []], comments: [null, [Validators.required]] }),
            plugDate: this.builder.group({ plugDate: [this.well.plugging.datePluggedAndAbandoned, []], comments: [null, [Validators.required]] }),
            replugDate: this.builder.group({ replugDate: [this.well.plugging.dateOfPlugBack, []], comments: [null, [Validators.required]] }),
            finalRestorationDate: this.builder.group({ finalRestorationDate: [this.finalRestorationDate, []], comments: [null, [Validators.required]] }),

            //Received Dates
            asDrilledPlatPreliminaryDate: this.builder.group({ asDrilledPlatPreliminaryDate: [(this.well.wellComplianceData == null) ? null : this.well.wellComplianceData.asDrilledPlatPreliminaryDate, []], comments: [null, [Validators.required]] }),
            asDrilledPlatFinalDate: this.builder.group({ asDrilledPlatFinalDate: [(this.well.wellComplianceData == null) ? null : this.well.wellComplianceData.asDrilledPlatFinalDate, []], comments: [null, [Validators.required]] }),
            boreholeDeviationSurveyDate: this.builder.group({ boreholeDeviationSurveyDate: [(this.well.wellComplianceData == null) ? null : this.well.wellComplianceData.boreholeDeviationSurveyDate, []], comments: [null, [Validators.required]] }),
            revisedUnitDate: this.builder.group({ revisedUnitDate: [(this.well.wellComplianceData == null) ? null : this.well.wellComplianceData.revisedUnitDate, []], comments: [null, [Validators.required]] }),
            form8PreliminaryReceivedDate: this.builder.group({ form8PreliminaryReceivedDate: [(this.well.wellComplianceData == null) ? null : this.well.wellComplianceData.form8PreliminaryReceivedDate, []], comments: [null, [Validators.required]] }),
            form8FinalReceivedDate: this.builder.group({ form8FinalReceivedDate: [(this.well.wellComplianceData == null) ? null : this.well.wellComplianceData.form8FinalReceivedDate, []], comments: [null, [Validators.required]] }),
            logsReceivedDate: this.builder.group({ logsReceivedDate: [(this.well.wellComplianceData == null) ? null : this.well.wellComplianceData.logsReceivedDate, []], comments: [null, [Validators.required]] }),
            form8ASurfaceHoleReceivedDate: this.builder.group({ form8ASurfaceHoleReceivedDate: [(this.well.wellComplianceData == null) ? null : this.well.wellComplianceData.form8ASurfaceHoleReceivedDate, []], comments: [null, [Validators.required]] }),
            form8BWellStimulationReceivedDate: this.builder.group({ form8BWellStimulationReceivedDate: [(this.well.wellComplianceData == null) ? null : this.well.wellComplianceData.form8BWellStimulationReceivedDate, []], comments: [null, [Validators.required]] }),
            cementTicketReceivedDate: this.builder.group({ cementTicketReceivedDate: [(this.well.wellComplianceData == null) ? null : this.well.wellComplianceData.cementTicketReceivedDate, []], comments: [null, [Validators.required]] }),
            cementLogReceivedDate: this.builder.group({ cementLogReceivedDate: [(this.well.wellComplianceData == null) ? null : this.well.wellComplianceData.cementLogReceivedDate, []], comments: [null, [Validators.required]] }),
            prGraphReceivedDate: this.builder.group({ prGraphReceivedDate: [(this.well.wellComplianceData == null) ? null : this.well.wellComplianceData.prGraphReceivedDate, []], comments: [null, [Validators.required]] }),
            stimInvoiceReceivedDate: this.builder.group({ stimInvoiceReceivedDate: [(this.well.wellComplianceData == null) ? null : this.well.wellComplianceData.stimInvoiceReceivedDate, []], comments: [null, [Validators.required]] }),
            stimRecordReceivedDate: this.builder.group({ stimRecordReceivedDate: [(this.well.wellComplianceData == null) ? null : this.well.wellComplianceData.stimRecordReceivedDate, []], comments: [null, [Validators.required]] }),
            stimLogReceivedDate: this.builder.group({ stimLogReceivedDate: [(this.well.wellComplianceData == null) ? null : this.well.wellComplianceData.stimLogReceivedDate, []], comments: [null, [Validators.required]] })

        })
    }

    findStatusDate(status: string[]): Date {
        if (this.well == null || this.well.wellStatus == null) return null;
        if (status.includes(this.well.wellStatus.disposition) === false) return null;
        return this.well.wellStatus.dateLastUpdated;
    }

    formatForm8AB(form: Form): string {
        return form.isRequired
            ? form.wasReceived
                ? "Received"
                : "Not Received"
            : "Not Required";
    }


    getEditIcon(formName: string, subForm: string = ""): string {
        let form: FormGroup = <FormGroup>this.editorForm.get(formName);
        if (subForm != null && subForm.length > 0) {
            form = <FormGroup>form.get(subForm);
        }
        return form != null && form.dirty ? "icon-notepad-alert" : "icon-notepad";
    }

    openEditor(controlName: string): void {
        let form: FormGroup = <FormGroup>this.editorForm;
        let controls: DialogControl[] = [];

        switch (controlName) {
            case "permitIssued": {
                let date = null;
                if (form.get('permitIssued').get('permitIssued').value != null) {
                    date = new Date(form.get('permitIssued').get('permitIssued').value);
                    date.setHours(date.getHours() + 5);
                }
                controls.push(new DialogControl("permitIssued", "Permit Issued", date, DialogControlType.Date, [], []));
                this.editorDialog.setFormName("permitIssued");
                break;
            }
            case "permitExpired": {
                let date = null;
                if (form.get('permitExpired').get('permitExpired').value != null) {
                    date = new Date(form.get('permitExpired').get('permitExpired').value);
                    date.setHours(date.getHours() + 5);
                }
                controls.push(new DialogControl("permitExpired", "Permit Expired", date, DialogControlType.Date, [], []));
                this.editorDialog.setFormName("permitExpired");
                break;
            }
            case "permitRevoked": {
                let date = null;
                if (form.get('permitRevoked').get('permitRevoked').value != null) {
                    date = new Date(form.get('permitRevoked').get('permitRevoked').value);
                    date.setHours(date.getHours() + 5);
                }
                controls.push(new DialogControl("permitRevoked", "Permit Revoked", date, DialogControlType.Date, [], []));
                this.editorDialog.setFormName("permitRevoked");
                break;
            }
            case "spudDate": {
                let date = null;
                if (form.get('spudDate').get('spudDate').value != null) {
                    date = new Date(form.get('spudDate').get('spudDate').value);
                    date.setHours(date.getHours() + 5);
                }
                controls.push(new DialogControl("spudDate", "Spud", date, DialogControlType.Date, [], []));
                this.editorDialog.setFormName("spudDate");
                break;
            }
            case "dateTotalDepthReached": {
                let date = null;
                if (form.get('dateTotalDepthReached').get('dateTotalDepthReached').value != null) {
                    date = new Date(form.get('dateTotalDepthReached').get('dateTotalDepthReached').value);
                    date.setHours(date.getHours() + 5);
                }
                controls.push(new DialogControl("dateTotalDepthReached", "Total Depth Reached", date, DialogControlType.Date, [], []));
                this.editorDialog.setFormName("dateTotalDepthReached");
                break;
            }
            case "dateOfFirstProduction": {
                let date = null;
                if (form.get('dateOfFirstProduction').get('dateOfFirstProduction').value != null) {
                    date = new Date(form.get('dateOfFirstProduction').get('dateOfFirstProduction').value);
                    date.setHours(date.getHours() + 5);
                }
                controls.push(new DialogControl("dateOfFirstProduction", "First Production", date, DialogControlType.Date, [], []));
                this.editorDialog.setFormName("dateOfFirstProduction");
                break;
            }
            case "plugDate": {
                let date = null;
                if (form.get('plugDate').get('plugDate').value != null) {
                    date = new Date(form.get('plugDate').get('plugDate').value);
                    date.setHours(date.getHours() + 5);
                }
                controls.push(new DialogControl("plugDate", "Plug Date", date, DialogControlType.Date, [], []));
                this.editorDialog.setFormName("plugDate");
                break;
            }
            case "replugDate": {
                let date = null;
                if (form.get('replugDate').get('replugDate').value != null) {
                    date = new Date(form.get('replugDate').get('replugDate').value);
                    date.setHours(date.getHours() + 5);
                }
                controls.push(new DialogControl("replugDate", "Replug Date", date, DialogControlType.Date, [], []));
                this.editorDialog.setFormName("replugDate");
                break;
            }
            case "finalRestorationDate": {
                let date = null;
                if (form.get('finalRestorationDate').get('finalRestorationDate').value != null) {
                    date = new Date(form.get('finalRestorationDate').get('finalRestorationDate').value);
                    date.setHours(date.getHours() + 5);
                }
                controls.push(new DialogControl("finalRestorationDate", "Final Restoration", date, DialogControlType.Date, [], []));
                this.editorDialog.setFormName("finalRestorationDate");
                break;
            }
            case "asDrilledPlatPreliminaryDate": {
                let date = null;
                if (form.get('asDrilledPlatPreliminaryDate').get('asDrilledPlatPreliminaryDate').value != null) {
                    date = new Date(form.get('asDrilledPlatPreliminaryDate').get('asDrilledPlatPreliminaryDate').value);
                    date.setHours(date.getHours() + 5);
                }
                controls.push(new DialogControl("asDrilledPlatPreliminaryDate", "As-Drilled Plat Preliminary", date, DialogControlType.Date, [], []));
                this.editorDialog.setFormName("asDrilledPlatPreliminaryDate");
                break;
            }
            case "asDrilledPlatFinalDate": {
                let date = null;
                if (form.get('asDrilledPlatFinalDate').get('asDrilledPlatFinalDate').value != null) {
                    date = new Date(form.get('asDrilledPlatFinalDate').get('asDrilledPlatFinalDate').value);
                    date.setHours(date.getHours() + 5);
                }
                controls.push(new DialogControl("asDrilledPlatFinalDate", "As-Drilled Plat Final", date, DialogControlType.Date, [], []));
                this.editorDialog.setFormName("asDrilledPlatFinalDate");
                break;
            }
            case "boreholeDeviationSurveyDate": {
                let date = null;
                if (form.get('boreholeDeviationSurveyDate').get('boreholeDeviationSurveyDate').value != null) {
                    date = new Date(form.get('boreholeDeviationSurveyDate').get('boreholeDeviationSurveyDate').value);
                    date.setHours(date.getHours() + 5);
                }
                controls.push(new DialogControl("boreholeDeviationSurveyDate", "Borehole Deviation Survey", date, DialogControlType.Date, [], []));
                this.editorDialog.setFormName("boreholeDeviationSurveyDate");
                break;
            }
            case "revisedUnitDate": {
                let date = null;
                if (form.get('revisedUnitDate').get('revisedUnitDate').value != null) {
                    date = new Date(form.get('revisedUnitDate').get('revisedUnitDate').value);
                    date.setHours(date.getHours() + 5);
                }
                controls.push(new DialogControl("revisedUnitDate", "Revised Unit", date, DialogControlType.Date, [], []));
                this.editorDialog.setFormName("revisedUnitDate");
                break;
            }
            case "form8PreliminaryReceivedDate": {
                let date = null;
                if (form.get('form8PreliminaryReceivedDate').get('form8PreliminaryReceivedDate').value != null) {
                    date = new Date(form.get('form8PreliminaryReceivedDate').get('form8PreliminaryReceivedDate').value);
                    date.setHours(date.getHours() + 5);
                }
                controls.push(new DialogControl("form8PreliminaryReceivedDate", "Form 8 Preliminary Received", date, DialogControlType.Date, [], []));
                this.editorDialog.setFormName("form8PreliminaryReceivedDate");
                break;
            }
            case "form8FinalReceivedDate": {
                let date = null;
                if (form.get('form8FinalReceivedDate').get('form8FinalReceivedDate').value != null) {
                    date = new Date(form.get('form8FinalReceivedDate').get('form8FinalReceivedDate').value);
                    date.setHours(date.getHours() + 5);
                }
                controls.push(new DialogControl("form8FinalReceivedDate", "Form 8 Final Received", date, DialogControlType.Date, [], []));
                this.editorDialog.setFormName("form8FinalReceivedDate");
                break;
            }
            case "logsReceivedDate": {
                let date = null;
                if (form.get('logsReceivedDate').get('logsReceivedDate').value != null) {
                    date = new Date(form.get('logsReceivedDate').get('logsReceivedDate').value);
                    date.setHours(date.getHours() + 5);
                }
                controls.push(new DialogControl("logsReceivedDate", "Logs Received", date, DialogControlType.Date, [], []));
                this.editorDialog.setFormName("logsReceivedDate");
                break;
            }
            case "form8ASurfaceHoleReceivedDate": {
                let date = null;
                if (form.get('form8ASurfaceHoleReceivedDate').get('form8ASurfaceHoleReceivedDate').value != null) {
                    date = new Date(form.get('form8ASurfaceHoleReceivedDate').get('form8ASurfaceHoleReceivedDate').value);
                    date.setHours(date.getHours() + 5);
                }
                controls.push(new DialogControl("form8ASurfaceHoleReceivedDate", "Form 8A Surface Hole Received", date, DialogControlType.Date, [], []));
                this.editorDialog.setFormName("form8ASurfaceHoleReceivedDate");
                break;
            }
            case "form8BWellStimulationReceivedDate": {
                let date = null;
                if (form.get('form8BWellStimulationReceivedDate').get('form8BWellStimulationReceivedDate').value != null) {
                    date = new Date(form.get('form8BWellStimulationReceivedDate').get('form8BWellStimulationReceivedDate').value);
                    date.setHours(date.getHours() + 5);
                }
                controls.push(new DialogControl("form8BWellStimulationReceivedDate", "Form 8B Well Stimulation Received", date, DialogControlType.Date, [], []));
                this.editorDialog.setFormName("form8BWellStimulationReceivedDate");
                break;
            }
            case "cementTicketReceivedDate": {
                let date = null;
                if (form.get('cementTicketReceivedDate').get('cementTicketReceivedDate').value != null) {
                    date = new Date(form.get('cementTicketReceivedDate').get('cementTicketReceivedDate').value);
                    date.setHours(date.getHours() + 5);
                }
                controls.push(new DialogControl("cementTicketReceivedDate", "Cement Ticket Received", date, DialogControlType.Date, [], []));
                this.editorDialog.setFormName("cementTicketReceivedDate");
                break;
            }
            case "cementLogReceivedDate": {
                let date = null;
                if (form.get('cementLogReceivedDate').get('cementLogReceivedDate').value != null) {
                    date = new Date(form.get('cementLogReceivedDate').get('cementLogReceivedDate').value);
                    date.setHours(date.getHours() + 5);
                }
                controls.push(new DialogControl("cementLogReceivedDate", "Cement Log Received", date, DialogControlType.Date, [], []));
                this.editorDialog.setFormName("cementLogReceivedDate");
                break;
            }
            case "prGraphReceivedDate": {
                let date = null;
                if (form.get('prGraphReceivedDate').get('prGraphReceivedDate').value != null) {
                    date = new Date(form.get('prGraphReceivedDate').get('prGraphReceivedDate').value);
                    date.setHours(date.getHours() + 5);
                }
                controls.push(new DialogControl("prGraphReceivedDate", "PR Graph Received", date, DialogControlType.Date, [], []));
                this.editorDialog.setFormName("prGraphReceivedDate");
                break;
            }
            case "stimInvoiceReceivedDate": {
                let date = null;
                if (form.get('stimInvoiceReceivedDate').get('stimInvoiceReceivedDate').value != null) {
                    date = new Date(form.get('stimInvoiceReceivedDate').get('stimInvoiceReceivedDate').value);
                    date.setHours(date.getHours() + 5);
                }
                controls.push(new DialogControl("stimInvoiceReceivedDate", "Stimulation Invoice Received", date, DialogControlType.Date, [], []));
                this.editorDialog.setFormName("stimInvoiceReceivedDate");
                break;
            }
            case "stimRecordReceivedDate": {
                let date = null;
                if (form.get('stimRecordReceivedDate').get('stimRecordReceivedDate').value != null) {
                    date = new Date(form.get('stimRecordReceivedDate').get('stimRecordReceivedDate').value);
                    date.setHours(date.getHours() + 5);
                }
                controls.push(new DialogControl("stimRecordReceivedDate", "Stimulation Record Received", date, DialogControlType.Date, [], []));
                this.editorDialog.setFormName("stimRecordReceivedDate");
                break;
            }
            case "stimLogReceivedDate": {
                let date = null;
                if (form.get('stimLogReceivedDate').get('stimLogReceivedDate').value != null) {
                    date = new Date(form.get('stimLogReceivedDate').get('stimLogReceivedDate').value);
                    date.setHours(date.getHours() + 5);
                }
                controls.push(new DialogControl("stimLogReceivedDate", "Stimulation Log Received", date, DialogControlType.Date, [], []));
                this.editorDialog.setFormName("stimLogReceivedDate");
                break;
            }
        }

        //after switch statement
        controls.push(new DialogControl("comments", "Comments", form.get(controlName).get('comments').value, DialogControlType.TextEditor, [], [Validators.required]));
        this.editorDialog.setControls(controls);
        this.editorDialog.toggleDialog(true);
        this.dialog.updateDialog(this.editorDialog);
    }

    saveUpdates(args: any) {
        console.log("Save update called, args:", args);
    }

    saveUpdate(args: any): void {
        let formName = args.name;
        let form: FormGroup = <FormGroup>args.form;
        let existing = <FormGroup>this.editorForm.get(formName);
        if (existing != null && existing.controls != null) {
            Object.keys(form.controls).forEach(updateKey => {

                let origin: AbstractControl = null;
                origin = existing.get(updateKey);
                if (origin != null) {
                    let changed: boolean = false;
                    let update = form.get(updateKey);
                    if (update != null) {
                        changed = origin.value !== update.value;
                        if (changed) {
                            origin.setValue(update.value);
                            origin.markAsDirty();
                            //this.formEdited.emit(true);
                        }
                    }

                }
            });
        }


    }


}
